import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, DollarSign, Users, Mail, TrendingUp,
  BarChart3, Calculator, Target, Zap, ChevronDown,
  ChevronUp, Info,
} from "lucide-react";

// ── Industry Benchmarks ──
const INDUSTRIES = [
  { value: "ecommerce",       label: "E-commerce / Retail",     openRate: 15.7, clickRate: 2.1, convRate: 1.8, avgOrderValue: 85  },
  { value: "saas",            label: "SaaS / Technology",       openRate: 21.3, clickRate: 2.8, convRate: 2.1, avgOrderValue: 120 },
  { value: "real_estate",     label: "Real Estate",             openRate: 19.2, clickRate: 1.9, convRate: 0.9, avgOrderValue: 350 },
  { value: "health",          label: "Health & Wellness",       openRate: 20.5, clickRate: 2.4, convRate: 1.6, avgOrderValue: 65  },
  { value: "education",       label: "Education / Courses",     openRate: 22.1, clickRate: 2.6, convRate: 2.4, avgOrderValue: 197 },
  { value: "agency",          label: "Agency / B2B Services",   openRate: 18.8, clickRate: 2.2, convRate: 1.2, avgOrderValue: 500 },
  { value: "local_services",  label: "Local Services",          openRate: 20.1, clickRate: 2.0, convRate: 1.4, avgOrderValue: 150 },
  { value: "nonprofit",       label: "Nonprofit / Fundraising", openRate: 24.5, clickRate: 2.9, convRate: 1.1, avgOrderValue: 45  },
  { value: "general",         label: "Other / General",         openRate: 19.5, clickRate: 2.3, convRate: 1.5, avgOrderValue: 100 },
];

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtNum(n: number): string {
  return n.toLocaleString("en-US");
}

function SliderField({ label, id, min, max, step, value, displayVal, onChange, hint }: {
  label: string; id: string; min: number; max: number; step?: number;
  value: number; displayVal: string; onChange: (v: number) => void; hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="text-xs font-semibold text-ink">{label}</label>
        <span className="text-sm font-bold text-coral font-mono">{displayVal}</span>
      </div>
      {hint && <p className="text-[10px] text-slate mb-2">{hint}</p>}
      <input id={id} type="range" min={min} max={max} step={step || 1} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full bg-sand accent-coral cursor-pointer" />
      <div className="flex justify-between text-[10px] text-slate/50 mt-1">
        <span>{min.toLocaleString()}</span>
        <span>{max.toLocaleString()}</span>
      </div>
    </div>
  );
}

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Email ROI Calculator",
  applicationCategory: "BusinessApplication",
  description: "Calculate the revenue potential of your email list. See projected monthly and annual earnings based on your list size, industry, and sending frequency.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function RoiCalculatorPage() {
  // Inputs
  const [listSize, setListSize] = useState(5000);
  const [industry, setIndustry] = useState("ecommerce");
  const [sendsPerMonth, setSendsPerMonth] = useState(8);
  const [avgOrderValue, setAvgOrderValue] = useState(85);
  const [useCustomRates, setUseCustomRates] = useState(false);
  const [customOpenRate, setCustomOpenRate] = useState(19.5);
  const [customClickRate, setCustomClickRate] = useState(2.3);
  const [customConvRate, setCustomConvRate] = useState(1.5);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const bench = INDUSTRIES.find(i => i.value === industry) || INDUSTRIES[8];

  // When industry changes, update AOV to benchmark
  const handleIndustryChange = (val: string) => {
    setIndustry(val);
    const b = INDUSTRIES.find(i => i.value === val);
    if (b && !useCustomRates) {
      setAvgOrderValue(b.avgOrderValue);
    }
  };

  // Calculations
  const results = useMemo(() => {
    const openRate = useCustomRates ? customOpenRate : bench.openRate;
    const clickRate = useCustomRates ? customClickRate : bench.clickRate;
    const convRate = useCustomRates ? customConvRate : bench.convRate;

    const totalEmailsSent = listSize * sendsPerMonth;
    const totalOpens = Math.round(totalEmailsSent * (openRate / 100));
    const totalClicks = Math.round(totalEmailsSent * (clickRate / 100));
    const totalConversions = Math.round(totalClicks * (convRate / 100));
    const monthlyRevenue = totalConversions * avgOrderValue;
    const annualRevenue = monthlyRevenue * 12;

    // Revenue per subscriber per month
    const revenuePerSub = listSize > 0 ? monthlyRevenue / listSize : 0;

    // Revenue per email sent
    const revenuePerEmail = totalEmailsSent > 0 ? monthlyRevenue / totalEmailsSent : 0;

    // Growth projections (assuming 5% monthly list growth)
    const growthRate = 0.05;
    const month3Revenue = monthlyRevenue * Math.pow(1 + growthRate, 3);
    const month6Revenue = monthlyRevenue * Math.pow(1 + growthRate, 6);
    const month12Revenue = monthlyRevenue * Math.pow(1 + growthRate, 12);

    // If they improved rates by 20%
    const improvedClicks = Math.round(totalEmailsSent * ((clickRate * 1.2) / 100));
    const improvedConversions = Math.round(improvedClicks * ((convRate * 1.2) / 100));
    const improvedMonthlyRevenue = improvedConversions * avgOrderValue;
    const uplift = improvedMonthlyRevenue - monthlyRevenue;

    return {
      openRate, clickRate, convRate,
      totalEmailsSent, totalOpens, totalClicks, totalConversions,
      monthlyRevenue, annualRevenue,
      revenuePerSub, revenuePerEmail,
      month3Revenue, month6Revenue, month12Revenue,
      improvedMonthlyRevenue, uplift,
    };
  }, [listSize, industry, sendsPerMonth, avgOrderValue, useCustomRates, customOpenRate, customClickRate, customConvRate, bench]);

  return (
    <>
      <SeoHead
        title="Email ROI Calculator — How Much Revenue Can Your Email List Generate? | MoreMail"
        description="Free email marketing ROI calculator. Enter your list size and industry to see projected monthly revenue, annual earnings, and growth projections. Includes industry benchmarks."
        canonical={`${SITE_URL}/roi-calculator`}
        keywords="email ROI calculator, email marketing revenue calculator, email list value calculator, email marketing ROI, how much is my email list worth, email revenue projections, email marketing benchmark"
        ogTitle="Email ROI Calculator — See What Your Email List Is Worth | MoreMail"
        ogDescription="Enter your list size. See your projected revenue. Free calculator with industry benchmarks for e-commerce, SaaS, real estate & more."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <Calculator size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            Email ROI <span className="text-coral">Calculator</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            See how much revenue your email list can generate. Adjust the inputs below and watch the numbers update instantly.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-6">

            {/* ── Inputs (left) ── */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <Target size={15} className="text-coral" /> Your Numbers
                </h2>

                <div className="space-y-5">
                  {/* Industry */}
                  <div>
                    <label className="block text-xs font-semibold text-ink mb-1.5">Industry</label>
                    <select value={industry} onChange={(e) => handleIndustryChange(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral">
                      {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                    </select>
                    <p className="text-[10px] text-slate mt-1">Sets benchmark open, click, and conversion rates</p>
                  </div>

                  <SliderField label="Email List Size" id="listSize" min={100} max={500000} step={100}
                    value={listSize} displayVal={fmtNum(listSize)} onChange={setListSize}
                    hint="Total active subscribers" />

                  <SliderField label="Emails per Month" id="sends" min={1} max={30}
                    value={sendsPerMonth} displayVal={`${sendsPerMonth}`} onChange={setSendsPerMonth}
                    hint="Campaigns + automated emails" />

                  <SliderField label="Average Order Value" id="aov" min={5} max={2000} step={5}
                    value={avgOrderValue} displayVal={`$${avgOrderValue}`} onChange={setAvgOrderValue}
                    hint="Revenue per conversion" />
                </div>
              </div>

              {/* Custom rates toggle */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <button onClick={() => setUseCustomRates(!useCustomRates)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-ink">
                  <span className="flex items-center gap-2">
                    <BarChart3 size={14} className="text-coral" />
                    {useCustomRates ? "Using Custom Rates" : "Using Industry Benchmarks"}
                  </span>
                  {useCustomRates ? <ChevronUp size={14} className="text-slate" /> : <ChevronDown size={14} className="text-slate" />}
                </button>

                {!useCustomRates && (
                  <div className="mt-3 flex gap-4 text-[11px] text-slate">
                    <span>Open: <strong className="text-ink">{bench.openRate}%</strong></span>
                    <span>Click: <strong className="text-ink">{bench.clickRate}%</strong></span>
                    <span>Conv: <strong className="text-ink">{bench.convRate}%</strong></span>
                  </div>
                )}

                {useCustomRates && (
                  <div className="mt-4 space-y-4">
                    <SliderField label="Open Rate" id="openRate" min={5} max={60} step={0.1}
                      value={customOpenRate} displayVal={`${customOpenRate.toFixed(1)}%`} onChange={setCustomOpenRate} />
                    <SliderField label="Click Rate" id="clickRate" min={0.1} max={15} step={0.1}
                      value={customClickRate} displayVal={`${customClickRate.toFixed(1)}%`} onChange={setCustomClickRate} />
                    <SliderField label="Conversion Rate" id="convRate" min={0.1} max={10} step={0.1}
                      value={customConvRate} displayVal={`${customConvRate.toFixed(1)}%`} onChange={setCustomConvRate} />
                  </div>
                )}
              </div>
            </div>

            {/* ── Results (right) ── */}
            <div className="lg:col-span-3 space-y-5">

              {/* Big numbers */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-sand p-5 text-center">
                  <DollarSign size={20} className="mx-auto text-coral mb-2" />
                  <p className="font-display font-bold text-2xl md:text-3xl text-ink">{fmt(results.monthlyRevenue)}</p>
                  <p className="text-xs text-slate mt-1">Monthly Revenue</p>
                </div>
                <div className="bg-ink rounded-xl p-5 text-center">
                  <TrendingUp size={20} className="mx-auto text-coral mb-2" />
                  <p className="font-display font-bold text-2xl md:text-3xl text-white">{fmt(results.annualRevenue)}</p>
                  <p className="text-xs text-white/50 mt-1">Annual Revenue</p>
                </div>
              </div>

              {/* Per-unit metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <p className="font-display font-bold text-lg text-ink">${results.revenuePerSub.toFixed(2)}</p>
                  <p className="text-[10px] text-slate mt-0.5">Rev / Subscriber / Mo</p>
                </div>
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <p className="font-display font-bold text-lg text-ink">${results.revenuePerEmail.toFixed(3)}</p>
                  <p className="text-[10px] text-slate mt-0.5">Rev / Email Sent</p>
                </div>
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <p className="font-display font-bold text-lg text-ink">{fmtNum(results.totalConversions)}</p>
                  <p className="text-[10px] text-slate mt-0.5">Conversions / Mo</p>
                </div>
              </div>

              {/* Funnel breakdown */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <button onClick={() => setShowBreakdown(!showBreakdown)}
                  className="w-full flex items-center justify-between">
                  <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2">
                    <Mail size={15} className="text-coral" /> Email Funnel Breakdown
                  </h3>
                  {showBreakdown ? <ChevronUp size={14} className="text-slate" /> : <ChevronDown size={14} className="text-slate" />}
                </button>

                {showBreakdown && (
                  <div className="mt-4 space-y-3">
                    {[
                      { label: "Emails Sent", value: fmtNum(results.totalEmailsSent), pct: "100%", color: "bg-slate/20", width: "100%" },
                      { label: "Opened", value: fmtNum(results.totalOpens), pct: `${results.openRate}%`, color: "bg-blue-400", width: `${Math.min(results.openRate * 2.5, 100)}%` },
                      { label: "Clicked", value: fmtNum(results.totalClicks), pct: `${results.clickRate}%`, color: "bg-amber-400", width: `${Math.min(results.clickRate * 10, 100)}%` },
                      { label: "Converted", value: fmtNum(results.totalConversions), pct: `${results.convRate}% of clicks`, color: "bg-coral", width: `${Math.min(results.convRate * 10, 100)}%` },
                    ].map((step) => (
                      <div key={step.label}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-ink">{step.label}</span>
                          <span className="text-xs text-slate">{step.value} <span className="text-slate/50">({step.pct})</span></span>
                        </div>
                        <div className="h-2 bg-paper rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${step.color} transition-all duration-500`} style={{ width: step.width }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Growth projections */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-4">
                  <TrendingUp size={15} className="text-coral" /> Growth Projections
                  <span className="text-[10px] text-slate font-normal ml-1">(5% monthly list growth)</span>
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "3 Months", value: results.month3Revenue },
                    { label: "6 Months", value: results.month6Revenue },
                    { label: "12 Months", value: results.month12Revenue },
                  ].map((proj) => (
                    <div key={proj.label} className="bg-paper rounded-lg p-3 text-center">
                      <p className="font-display font-bold text-ink text-lg">{fmt(proj.value)}</p>
                      <p className="text-[10px] text-slate mt-0.5">{proj.label} / mo</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optimization uplift */}
              <div className="bg-gradient-to-r from-teal/10 to-teal-light rounded-xl border border-teal/15 p-5">
                <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-2">
                  <Zap size={15} className="text-teal" /> If You Optimized by 20%...
                </h3>
                <p className="text-sm text-slate mb-3">
                  Improving your click and conversion rates by just 20% (achievable with A/B testing and better targeting) would add:
                </p>
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-lg px-4 py-2 border border-teal/20">
                    <p className="font-display font-bold text-teal text-xl">+{fmt(results.uplift)}</p>
                    <p className="text-[10px] text-slate">Extra monthly revenue</p>
                  </div>
                  <div className="bg-white rounded-lg px-4 py-2 border border-teal/20">
                    <p className="font-display font-bold text-teal text-xl">+{fmt(results.uplift * 12)}</p>
                    <p className="text-[10px] text-slate">Extra annual revenue</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Benchmarks table */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-3">
            Email Marketing <span className="text-coral">Industry Benchmarks</span>
          </h2>
          <p className="text-sm text-slate text-center mb-8 max-w-lg mx-auto">
            How does your industry stack up? These are average rates across thousands of email campaigns.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-sand">
                  <th className="text-left py-3 px-3 text-xs font-semibold text-slate">Industry</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Open Rate</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Click Rate</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Conv Rate</th>
                  <th className="text-right py-3 px-3 text-xs font-semibold text-slate">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {INDUSTRIES.map((ind) => (
                  <tr key={ind.value}
                    className={`border-b border-sand/50 transition-colors cursor-pointer hover:bg-paper ${industry === ind.value ? "bg-coral-light/30" : ""}`}
                    onClick={() => handleIndustryChange(ind.value)}>
                    <td className="py-2.5 px-3 font-medium text-ink text-xs">{ind.label}</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{ind.openRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{ind.clickRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">{ind.convRate}%</td>
                    <td className="py-2.5 px-3 text-right text-xs text-slate">${ind.avgOrderValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate/50 text-center mt-3">
            Click any row to apply that industry's benchmarks to the calculator above.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Ready to Hit Those Numbers?
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            MoreMail gives you the AI tools, pre-built campaigns, and automation workflows
            to turn your email list into a revenue machine.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
